/* ============================================================
   VITALCASA CRM — ModalCita (tema claro/oscuro)
   Modal para programar visitas a propiedades con un cliente.
   
   Flujo:
   1. Seleccionar fecha, hora de inicio, duración por visita
   2. Lista de propiedades reordenable (drag & drop)
      → cada propiedad muestra su hora calculada automáticamente
   3. Elegir quién cierra la cita: Agente o Ana (IA)
      → Agente: muestra datos de contacto del cliente y propietarios
      → Ana: pregunta si por WhatsApp o Email, luego confirma
   
   Props:
   - isOpen: boolean
   - onClose: () => void
   - onConfirm: ({ fecha, horaInicio, horaFin, duracion, propiedades, cierreMode, cierreCanal }) => void
   - contact: { nombre, telefono, email, iniciales }
   - properties: [{ id, titulo, detalle, precio, icono, referencia, info_visita,
                     propietarios: [{ nombre, telefono, email }] }]
   - agentName: string
   - onLlamarTelefono: (telefono) => void — Telnyx CRM (no tel: del sistema)
   - onAbrirVistaPrevia: (propiedadId) => void — Vista previa de ficha
   ============================================================ */

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTheme } from './ThemeProvider';
import CitaLinkTelefono from "./cita/CitaLinkTelefono";
import CitaLinkReferencia from "./cita/CitaLinkReferencia";
import CitaLinkEmail from "./cita/CitaLinkEmail";
import CitaBloquePersona from "./cita/CitaBloquePersona";
import CitaSelectorHora from "./cita/CitaSelectorHora";
import ModalAvisoLlavesCitas from "./cita/ModalAvisoLlavesCitas";
import { isPropiedadTextoLibre, mapPropiedadTextoLibre } from "../utils/citaAgenda";
import api from "../axiosConfig";

// ── Helpers ─────────────────────────────────────────────────
function addMinutes(timeStr, mins) {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + mins;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatFecha(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function getTodayStr() {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

function getNowTimeStr() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function calcHoraFinAuto(horaInicio, numProps, duracionMin) {
  if (!horaInicio) return "";
  // Sin propiedades: 1 bloque de duración (cita placeholder / a completar).
  const n = Math.max(1, Number(numProps) || 0);
  return addMinutes(horaInicio, n * (Number(duracionMin) || 45));
}

/** Acepta array desde API, JSON string, o filas con nombre+apellidos. */
function normalizarPropietarios(raw) {
  if (!raw) return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const nombre = [row.nombre, row.apellidos].filter(Boolean).join(' ').trim();
      return {
        nombre: nombre || 'Propietario',
        telefono: row.telefono != null ? String(row.telefono) : '',
        email: row.email != null ? String(row.email) : '',
        contacto_id: row.contacto_id || row.id || null,
        cliente_id: row.cliente_id || null,
      };
    })
    .filter(Boolean);
}

// ── Duraciones disponibles ──────────────────────────────────
const DURACIONES = [
  { min: 30, label: '30 min' },
  { min: 45, label: '45 min' },
  { min: 60, label: '1 hora' },
  { min: 90, label: '1h 30m' },
];

// ── Colores Cita (cyan UI + semánticos contacto/ref) ────────
// Morado no se lee bien en fondo oscuro → cyan para acentos de UI.
const ci = {
  cyan:       '#06b6d4',
  cyanFrom:   '#0891b2',
  cyanTo:     '#06b6d4',
  cyanLight:  '#22d3ee',
  cyanText:   '#ecfeff',
  cyanShadow: 'rgba(6,182,212,0.35)',
  cyanBg:     'rgba(6,182,212,0.12)',
  cyanBorder: 'rgba(6,182,212,0.30)',
  chipText:   '#67e8f9',
};
/** Teléfono → amarillo; email → naranja; Ref. → verde */
const CITA_TEL = '#eab308';
const CITA_MAIL = '#f97316';
const CITA_REF = '#22c55e';

const INFO_REF_GREEN = '#15803d';
const INFO_HEADER_RED = '#b91c1c';

function propTieneLlaves(p) {
  if (!p) return false;
  const v = p.tenemos_llaves;
  return v === true || v === 1 || v === '1' || v === 't' || v === 'true' || v === 'TRUE';
}

function BadgeLlavesPropiedad({ p, style }) {
  if (!propTieneLlaves(p)) return null;
  const num = p.num_llave != null && String(p.num_llave).trim() !== ''
    ? String(p.num_llave).trim()
    : '';
  return (
    <span
      style={{
        padding: '1px 7px',
        borderRadius: 4,
        fontSize: 9,
        fontWeight: 700,
        background: 'rgba(34,197,94,0.15)',
        color: '#16a34a',
        border: '1px solid rgba(34,197,94,0.35)',
        whiteSpace: 'nowrap',
        ...style,
      }}
      title={p.recoger_llaves_en ? `Recoger en: ${p.recoger_llaves_en}` : 'Tenemos llaves'}
    >
      🔑 Llaves{num ? ` nº ${num}` : ''}
    </span>
  );
}

function InfoVisitaCard({ p, onAbrirVistaPrevia }) {
  const texto = String(p.info_visita || '').trim();
  if (!texto) return null;
  return (
    <div
      style={{
        width: 200,
        flexShrink: 0,
        borderRadius: 8,
        overflow: 'hidden',
        border: '2px solid #ea580c',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          background: INFO_HEADER_RED,
          color: '#ffffff',
          padding: '8px 6px',
          fontSize: 9,
          fontWeight: 900,
          textAlign: 'center',
          lineHeight: 1.25,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
        }}
      >
        ATENCION!!! REVISE LA INFORMACION PARA LA VISITA
      </div>
      <div style={{ background: '#ffffff', padding: '8px 10px', flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: INFO_REF_GREEN, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>Ref.</span>
          <CitaLinkReferencia
            referencia={p.referencia || p.id}
            onAbrir={onAbrirVistaPrevia ? () => onAbrirVistaPrevia(p.id) : null}
            color={INFO_REF_GREEN}
          />
        </div>
        <div style={{ fontSize: 11, color: '#000000', whiteSpace: 'pre-wrap', lineHeight: 1.45, fontWeight: 500 }}>
          {texto}
        </div>
      </div>
    </div>
  );
}

function BloqueContactosVisita({
  propsOrdenadas,
  contact,
  horaInicio,
  duracion,
  c,
  onLlamarTelefono,
  onAbrirFichaCliente,
  onAbrirFichaContacto,
  onActivarFichaDetras,
  onAbrirVistaPrevia,
  onPropRowRef,
}) {
  if (!propsOrdenadas.length) return null;
  return (
    <div style={{ padding: '14px 16px', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'block', marginBottom: 10, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: ci.cyan }}>
        Contactos para la visita
      </div>
      <CitaBloquePersona
        etiqueta="Cliente"
        nombre={contact?.nombre}
        telefono={contact?.telefono}
        email={contact?.email}
        clienteId={contact?.clienteId || null}
        contactoId={contact?.contactoId || null}
        c={c}
        phoneColor={CITA_TEL}
        emailColor={CITA_MAIL}
        onLlamarTelefono={onLlamarTelefono}
        onAbrirFichaCliente={onAbrirFichaCliente}
        onAbrirFichaContacto={onAbrirFichaContacto}
        onActivarFichaDetras={onActivarFichaDetras}
      />
      {propsOrdenadas.map((p, i) => (
        <div
          key={`prop-${p.id}`}
          ref={(el) => onPropRowRef?.(p.id, el)}
          style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${c.border}`, background: c.bgInput, marginBottom: 6, marginTop: 6 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 4, background: ci.cyanBg, color: ci.chipText }}>Propietario</span>
            {(p.referencia || p.id) ? (
              <CitaLinkReferencia
                referencia={p.referencia || p.id}
                onAbrir={onAbrirVistaPrevia && !isPropiedadTextoLibre(p) ? () => onAbrirVistaPrevia(p.id) : null}
                color={CITA_REF}
                style={{ fontSize: 12, fontWeight: 600 }}
              />
            ) : null}
            {(p.referencia || p.id) && (p.titulo && String(p.titulo).trim()) ? (
              <span style={{ color: c.textMuted, fontSize: 12 }}>·</span>
            ) : null}
            <span style={{ fontSize: 12, fontWeight: 600, color: c.textPrimary }}>{(p.titulo && String(p.titulo).trim()) || 'Sin título'}</span>
            <span style={{ fontSize: 10, color: c.textMuted }}>{addMinutes(horaInicio, i * duracion)}</span>
          </div>
          {(() => {
            const listaProp = normalizarPropietarios(p.propietarios);
            const badgeLlaves = <BadgeLlavesPropiedad p={p} />;
            return listaProp.length > 0 ? listaProp.map((prop, j) => (
              <div key={j} style={{ marginLeft: 4, marginBottom: j < listaProp.length - 1 ? 8 : 0 }}>
                <CitaBloquePersona
                  nombre={prop.nombre}
                  telefono={prop.telefono}
                  email={prop.email}
                  clienteId={prop.cliente_id || null}
                  contactoId={prop.contacto_id || null}
                  c={c}
                  phoneColor={CITA_TEL}
                  emailColor={CITA_MAIL}
                  onLlamarTelefono={onLlamarTelefono}
                  onAbrirFichaCliente={onAbrirFichaCliente}
                  onAbrirFichaContacto={onAbrirFichaContacto}
                  onActivarFichaDetras={onActivarFichaDetras}
                  extraTrasTelefono={j === 0 ? badgeLlaves : null}
                  compact
                />
              </div>
            )) : (
              <div style={{ marginLeft: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, color: c.textMuted, fontStyle: 'italic' }}>Sin propietarios registrados</div>
                {badgeLlaves}
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}

export default function ModalCita({
  isOpen,
  onClose,
  onConfirm,
  contact,
  properties = [],
  agentName = "",
  onLlamarTelefono,
  onAbrirVistaPrevia,
  onAbrirFichaCliente,
  onAbrirFichaContacto,
  fichaDetras = false,
  onBuscarPropiedadPorRef,
  zIndex = 1000,
  modoEdicion = false,
  initialValues = null,
  /** Nombre de cliente a mano (sin ficha CRM); si no, se usa contact.nombre. */
  initialClienteTexto = null,
  footerExtra = null,
  readOnly = false,
  /** Si false, el clic en el fondo no cierra (p. ej. segunda visita). */
  cerrarAlClicFuera = true,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  // ── Estado ──────────────────────────────────────
  const [fecha, setFecha] = useState(getTodayStr());
  const [horaInicio, setHoraInicio] = useState(getNowTimeStr);
  const [horaFin, setHoraFin] = useState(getNowTimeStr);
  const [horaFinManual, setHoraFinManual] = useState(false);
  const [duracion, setDuracion] = useState(45);
  const [propsOrdenadas, setPropsOrdenadas] = useState(properties);
  const [clienteTexto, setClienteTexto] = useState(
    () => String(initialClienteTexto ?? contact?.nombre ?? '').trim(),
  );
  const [confirmado, setConfirmado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errGuardar, setErrGuardar] = useState('');
  const [fechaFocus, setFechaFocus] = useState(false);
  // Añadir propiedad por referencia o texto libre
  const [addRef, setAddRef] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState('');
  const [fichaVisibleDetras, setFichaVisibleDetras] = useState(false);
  const [avisoLlaves, setAvisoLlaves] = useState(null); // { contexto }
  const avisadosLlavesRef = useRef(new Set());
  const avisoLlavesResolveRef = useRef(null);

  const activarFichaDetras = useCallback(() => {
    setFichaVisibleDetras(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFichaVisibleDetras(false);
    } else if (fichaDetras) {
      setFichaVisibleDetras(true);
    }
  }, [isOpen, fichaDetras]);

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const modalClusterRef = useRef(null);
  const propContactRefs = useRef({});
  const [infoCardTops, setInfoCardTops] = useState({});
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const [horaFocus, setHoraFocus] = useState(false);
  const [horaFinFocus, setHoraFinFocus] = useState(false);

  // Drag & drop reordenar lista
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const modalInicializadoRef = useRef(false);
  // "Ahora" congelado por apertura del modal: evita que la hora fin se recalcule
  // contra el reloj (hora actual + duración) y "se mueva" sola. Se refresca al cerrar.
  const nowFrozenRef = useRef(null);
  // Cuando las propiedades llegan de forma asíncrona DESPUÉS de abrir el modal (p. ej.
  // CitaDesdeFrioModal las carga por API), el init ya se ejecutó con la lista vacía.
  // Este ref permite sincronizarlas una sola vez sin pisar ediciones del usuario.
  const propsLlegadasRef = useRef(false);
  const propsExternasSyncRef = useRef([]);

  // Propiedades añadidas desde la ficha (modo fichaDetras / overlay global)
  useEffect(() => {
    if (!isOpen) {
      propsExternasSyncRef.current = [];
      return;
    }
    const prev = propsExternasSyncRef.current || [];
    const nuevas = (properties || []).filter(
      (p) => !prev.some((x) => String(x.id) === String(p.id)),
    );
    propsExternasSyncRef.current = properties || [];
    if (!nuevas.length) return;
    setPropsOrdenadas((lista) => {
      const ids = new Set(lista.map((p) => String(p.id)));
      const merged = [...lista];
      for (const p of nuevas) {
        if (!ids.has(String(p.id))) merged.push(p);
      }
      if (!horaFinManual && merged.length !== lista.length) {
        setHoraFin(calcHoraFinAuto(horaInicio, merged.length, duracion));
      }
      return merged;
    });
  }, [isOpen, properties, horaFinManual, horaInicio, duracion]);

  useEffect(() => {
    if (!isOpen) {
      modalInicializadoRef.current = false;
      nowFrozenRef.current = null;
      propsLlegadasRef.current = false;
      setGuardando(false);
      setErrGuardar('');
      setAddRef('');
      setAddErr('');
      setAddBusy(false);
      propContactRefs.current = {};
      setInfoCardTops({});
      setHoraFinManual(false);
      avisadosLlavesRef.current = new Set();
      setAvisoLlaves(null);
      if (avisoLlavesResolveRef.current) {
        try { avisoLlavesResolveRef.current(); } catch { /* noop */ }
        avisoLlavesResolveRef.current = null;
      }
      return;
    }
    if (modalInicializadoRef.current) return;
    modalInicializadoRef.current = true;

    if (!nowFrozenRef.current) nowFrozenRef.current = getNowTimeStr();
    const hi = initialValues?.horaInicio || nowFrozenRef.current;
    const dur = initialValues?.duracion ?? 45;
    const nProps = properties.length;
    const fechaInit = initialValues?.fecha || getTodayStr();
    setFecha(fechaInit);
    setHoraInicio(hi);
    setDuracion(dur);
    if (initialValues?.horaFin) {
      setHoraFin(initialValues.horaFin);
      setHoraFinManual(true);
    } else {
      setHoraFinManual(false);
      setHoraFin(calcHoraFinAuto(hi, nProps, dur));
    }
    setPropsOrdenadas(properties);
    setClienteTexto(String(initialClienteTexto ?? contact?.nombre ?? '').trim());
  }, [isOpen, properties, initialValues, initialClienteTexto, contact?.nombre]);

  const cerrarAvisoLlaves = useCallback(() => {
    setAvisoLlaves(null);
    const resolve = avisoLlavesResolveRef.current;
    avisoLlavesResolveRef.current = null;
    if (typeof resolve === 'function') {
      try { resolve(); } catch { /* noop */ }
    }
  }, []);

  const mostrarAvisoLlavesSiProcede = useCallback(async (prop, alrededorOverride) => {
    if (!prop?.id || isPropiedadTextoLibre(prop)) return;
    if (!prop.tenemos_llaves) return;
    const pid = String(prop.id);
    if (avisadosLlavesRef.current.has(pid)) return;
    avisadosLlavesRef.current.add(pid);
    try {
      const alrededor = alrededorOverride
        || (fecha && horaInicio ? `${fecha}T${horaInicio}:00` : new Date().toISOString());
      const res = await api.get(`/propiedades/${encodeURIComponent(pid)}/citas-contexto-llaves`, {
        params: { alrededor },
      });
      const data = res.data?.data || res.data;
      if (!data) return;
      await new Promise((resolve) => {
        avisoLlavesResolveRef.current = resolve;
        setAvisoLlaves({ contexto: data });
      });
    } catch (e) {
      console.warn('[ModalCita] aviso llaves:', e?.message || e);
    }
  }, [fecha, horaInicio]);

  // Aviso llaves para propiedades ya presentes al abrir (ficha / edición)
  const propsIdsKey = Array.isArray(properties)
    ? properties.map((p) => p?.id).filter(Boolean).join(',')
    : '';
  useEffect(() => {
    if (!isOpen || !modalInicializadoRef.current) return;
    const alrededor = fecha && horaInicio ? `${fecha}T${horaInicio}:00` : undefined;
    const lista = Array.isArray(properties) ? properties : [];
    let cancelled = false;
    (async () => {
      for (const p of lista) {
        if (cancelled) break;
        await mostrarAvisoLlavesSiProcede(p, alrededor);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir / cambiar set de props
  }, [isOpen, propsIdsKey, mostrarAvisoLlavesSiProcede]);

  const recalcFinAuto = (hi, n, dur) => {
    setHoraFin(calcHoraFinAuto(hi, n, dur));
  };

  // Carga diferida: si el modal se abrió sin propiedades y llegan después por API,
  // sincronízalas una vez (solo si la lista interna sigue vacía: no pisa ediciones)
  // y recalcula la hora fin con el nº real de propiedades (salvo que sea manual).
  useEffect(() => {
    if (!isOpen || propsLlegadasRef.current) return;
    if (propsOrdenadas.length === 0 && properties.length > 0) {
      propsLlegadasRef.current = true;
      setPropsOrdenadas(properties);
      if (!horaFinManual) setHoraFin(calcHoraFinAuto(horaInicio, properties.length, duracion));
    }
  }, [isOpen, properties, propsOrdenadas.length, horaFinManual, horaInicio, duracion]);

  // IMPORTANTE: no hay recálculo automático por efecto. La hora fin se recalcula SOLO
  // ante acciones explícitas del usuario (cambiar inicio/duración, añadir/quitar
  // propiedad) o al llegar las propiedades diferidas. Así nunca se recalcula sola al
  // re-renderizar/re-montar el modal, y no puede arrastrar el reloj (ahora+duración).

  const aplicarHoraInicio = (v) => {
    setHoraInicio(v);
    setHoraFinManual(false);
    recalcFinAuto(v, propsOrdenadas.length, duracion);
  };

  const aplicarDuracion = (min) => {
    setDuracion(min);
    setHoraFinManual(false);
    recalcFinAuto(horaInicio, propsOrdenadas.length, min);
  };

  const alignInfoCards = useCallback(() => {
    const cluster = modalClusterRef.current;
    if (!cluster) return;
    const clusterRect = cluster.getBoundingClientRect();
    const tops = {};
    propsOrdenadas.forEach((p) => {
      if (!String(p.info_visita || '').trim()) return;
      const el = propContactRefs.current[p.id];
      if (el) {
        const rowRect = el.getBoundingClientRect();
        tops[p.id] = Math.max(0, rowRect.top - clusterRect.top);
      }
    });
    setInfoCardTops(tops);
  }, [propsOrdenadas]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    alignInfoCards();
    const t = requestAnimationFrame(alignInfoCards);
    window.addEventListener('resize', alignInfoCards);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener('resize', alignInfoCards);
    };
  }, [isOpen, alignInfoCards, pos, propsOrdenadas.length, horaInicio, duracion]);

  const handlePropRowRef = useCallback((id, el) => {
    if (el) propContactRefs.current[id] = el;
    else delete propContactRefs.current[id];
  }, []);

  if (!isOpen) return null;

  // ── Drag & Drop ─────────────────────────────────
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const newList = [...propsOrdenadas];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(idx, 0, moved);
    setPropsOrdenadas(newList);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const removeProp = (id) => {
    const next = propsOrdenadas.filter((p) => p.id !== id);
    setPropsOrdenadas(next);
    setHoraFinManual(false);
    recalcFinAuto(horaInicio, next.length, duracion);
  };

  const agregarPropiedadTextoLibre = (texto) => {
    const stub = mapPropiedadTextoLibre(texto);
    if (!stub) return false;
    if (propsOrdenadas.some((p) => {
      const label = String(p.titulo || p.referencia || '').trim().toLowerCase();
      return label && label === stub.titulo.toLowerCase();
    })) {
      setAddErr('Esa propiedad ya está en la lista.');
      return false;
    }
    const next = [...propsOrdenadas, stub];
    setPropsOrdenadas(next);
    setHoraFinManual(false);
    recalcFinAuto(horaInicio, next.length, duracion);
    setAddRef('');
    setAddErr('');
    return true;
  };

  const agregarPorRef = async () => {
    const ref = String(addRef || '').trim();
    if (!ref || addBusy) return;
    setAddBusy(true);
    setAddErr('');
    try {
      let mapped = null;
      let lookupErr = null;
      if (typeof onBuscarPropiedadPorRef === 'function') {
        try {
          mapped = await onBuscarPropiedadPorRef(ref);
        } catch (e) {
          lookupErr = e;
          mapped = null;
        }
      }
      if (mapped?.id) {
        if (propsOrdenadas.some((p) => String(p.id) === String(mapped.id))) {
          setAddErr('Esa propiedad ya está en la lista.');
          return;
        }
        await mostrarAvisoLlavesSiProcede(mapped);
        const next = [...propsOrdenadas, mapped];
        setPropsOrdenadas(next);
        setHoraFinManual(false);
        recalcFinAuto(horaInicio, next.length, duracion);
        setAddRef('');
        return;
      }
      // Parece una ref CRM (p. ej. 3598-C): no convertir a texto a ciegas.
      const pareceRefCrm = /^\d{2,6}\s*[-–]?\s*[A-Za-z]$/i.test(ref)
        || /^\d{2,6}[-–][A-Za-z0-9]{1,4}$/i.test(ref);
      if (pareceRefCrm) {
        setAddErr(
          (lookupErr && lookupErr.message)
            ? lookupErr.message
            : `No se encontró la ref. «${ref}» en el CRM. Comprueba mayúsculas (p. ej. 3598-C) o pulsa «Como texto».`
        );
        return;
      }
      if (!agregarPropiedadTextoLibre(ref)) return;
    } catch (e) {
      setAddErr(e?.message || 'No se pudo añadir la propiedad.');
    } finally {
      setAddBusy(false);
    }
  };

  const agregarComoTextoLibre = () => {
    const ref = String(addRef || '').trim();
    if (!ref || addBusy || readOnly) return;
    setAddErr('');
    agregarPropiedadTextoLibre(ref);
  };

  // ── Confirmar ───────────────────────────────────
  const handleConfirm = async () => {
    if (guardando || readOnly) return;
    setErrGuardar('');
    setGuardando(true);
    try {
      const payload = {
        fecha, horaInicio, horaFin, duracion,
        propiedades: propsOrdenadas.map((p, i) => ({ ...p, horaVisita: addMinutes(horaInicio, i * duracion) })),
        clienteTexto: String(clienteTexto || '').trim(),
        cierreMode: 'agente',
        cierreCanal: null,
      };
      await onConfirm?.(payload);
      setConfirmado(true);
      setTimeout(() => {
        setConfirmado(false);
        onClose?.();
      }, 2000);
    } catch (e) {
      setErrGuardar(e?.message || 'No se pudo guardar la cita');
    } finally {
      setGuardando(false);
    }
  };

  // Propiedad y cliente son recomendables, no obligatorios (p. ej. hueco en agenda a completar).
  const puedeConfirmar = !readOnly;

  const subtitulo = propsOrdenadas.length
    ? `${propsOrdenadas.length} propiedad${propsOrdenadas.length !== 1 ? 'es' : ''} · Planificar visitas`
    : 'Sin propiedades aún · Puedes confirmar la cita igual';

  const sectionCard = {
    margin: '4px 12px 0',
    padding: '10px 12px',
    background: isDark ? 'transparent' : '#ffffff',
    borderRadius: 12,
    border: isDark ? 'none' : '1px solid #c8c8be',
    borderLeft: isDark ? 'none' : `4px solid ${ci.cyan}`,
    boxShadow: isDark ? 'none' : '0 3px 10px rgba(0,0,0,0.12)',
  };
  const sectionLabel = {
    display: 'block',
    marginBottom: 6,
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ci.cyanLight,
  };
  const fieldSublabel = {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: c.textMuted,
    opacity: 0.7,
  };

  // ── RENDER ──────────────────────────────────────
  const permitirInteraccionFicha = fichaVisibleDetras || fichaDetras;
  const modoCompactoFicha = Boolean(fichaDetras);

  const estiloModalPanel = {
    width: modoCompactoFicha ? 'min(420px, 94vw)' : 'min(1040px, 96vw)',
    maxHeight: modoCompactoFicha ? '78vh' : '92vh',
    display: 'flex',
    flexDirection: 'column',
    background: isDark ? c.bgCard : '#c8c8be',
    borderRadius: 16,
    overflow: 'hidden',
    border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
    boxShadow: isDark
      ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10), 0 0 200px 80px rgba(255,255,255,0.05), 0 0 300px 120px rgba(255,255,255,0.02)'
      : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
    position: (pos || modoCompactoFicha) ? 'fixed' : 'relative',
    pointerEvents: 'auto',
    ...(modoCompactoFicha && !pos ? { right: 16, bottom: 16, left: 'auto', top: 'auto', margin: 0 } : {}),
    ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
  };

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0, zIndex,
      fontFamily: "'DM Sans', sans-serif",
      pointerEvents: permitirInteraccionFicha ? 'none' : 'auto',
    }}>
      <div
        aria-hidden
        onClick={
          permitirInteraccionFicha || cerrarAlClicFuera === false
            ? undefined
            : onClose
        }
        style={{
          position: 'absolute', inset: 0,
          background: permitirInteraccionFicha ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.85)',
          pointerEvents: permitirInteraccionFicha ? 'none' : 'auto',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          boxSizing: 'border-box',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={modalClusterRef}
          style={{
            position: 'relative',
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            maxWidth: '100%',
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
        <div
          ref={dragRef}
          onClick={(e) => e.stopPropagation()}
          style={estiloModalPanel}
        >

        {/* Confirmación final */}
        {confirmado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>📅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: ci.cyan }}>
              {modoEdicion ? 'Cita actualizada' : 'Cita programada'}
            </div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500, textAlign: 'center', padding: '0 24px' }}>
              {modoEdicion ? 'Los cambios quedaron guardados en agenda.' : 'La visita quedó guardada en agenda.'}
            </div>
          </div>
        )}

        {/* Barra cyan superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${ci.cyanLight}, ${ci.cyan}, ${ci.cyanFrom}, ${ci.cyan}, ${ci.cyanLight})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${ci.cyanFrom}, ${ci.cyanTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, border: `1px solid ${c.border}`,
              boxShadow: `0 4px 12px ${ci.cyanShadow}`,
            }}>📅</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>
                {readOnly ? 'Ver visita (solo lectura)' : modoEdicion ? 'Editar visitas' : 'Programar visitas'}
              </h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, fontWeight: 500, color: c.textMuted }}>
                {readOnly ? 'No tienes permiso para modificar esta cita' : modoEdicion ? 'Modifica fecha, horario o propiedades de la cita' : subtitulo}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Indicador de paso único */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '4px 24px 0', flexShrink: 0 }}>
          <div style={{ width: 24, height: 8, borderRadius: 4, background: ci.cyan }} />
        </div>

        {/* Cuerpo en dos columnas — sin scroll global */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: modoCompactoFicha ? 'column' : 'row',
          minHeight: 0,
          overflow: 'hidden',
        }}>

          {/* Columna izquierda: planificación (imagen 1) */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', padding: isDark ? 0 : '4px 0 8px', borderRight: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>

            {/* Cliente (opcional; texto libre o ficha CRM) */}
            <div style={{ ...sectionCard, marginTop: 6 }}>
              <div style={sectionLabel}>Cliente <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, opacity: 0.75 }}>(opcional)</span></div>
              {!readOnly && (
                <input
                  type="text"
                  value={clienteTexto}
                  onChange={(e) => setClienteTexto(e.target.value)}
                  placeholder="Nombre a mano (aunque no esté en el CRM)"
                  style={{
                    width: '100%', boxSizing: 'border-box', marginBottom: 8,
                    padding: '8px 10px', borderRadius: 10,
                    border: `2px solid ${c.border}`, background: c.bgInput,
                    color: c.textPrimary, fontSize: 13, fontWeight: 600,
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              )}
              {(contact?.clienteId || contact?.contactoId || contact?.telefono || contact?.email) && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 10,
                  border: `2px solid ${c.border}`, background: c.bgInput,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${c.accentBlue}, ${c.accentBlueDark})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>{contact?.iniciales || '?'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <CitaBloquePersona
                      nombre={clienteTexto || contact?.nombre}
                      telefono={contact?.telefono}
                      email={contact?.email}
                      clienteId={contact?.clienteId || null}
                      contactoId={contact?.contactoId || null}
                      c={c}
                      phoneColor={CITA_TEL}
                      emailColor={CITA_MAIL}
                      onLlamarTelefono={onLlamarTelefono}
                      onAbrirFichaCliente={onAbrirFichaCliente}
                      onAbrirFichaContacto={onAbrirFichaContacto}
                      onActivarFichaDetras={activarFichaDetras}
                      compact
                    />
                  </div>
                </div>
              )}
              {readOnly && !contact?.clienteId && !contact?.contactoId && (
                <div style={{ fontSize: 13, fontWeight: 600, color: c.textMuted, padding: '6px 2px' }}>
                  {clienteTexto || 'Sin cliente'}
                </div>
              )}
            </div>

            {/* Fecha + Hora */}
            <div style={sectionCard}>
              <div style={sectionLabel}>Fecha y hora</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 120px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={fieldSublabel}>Fecha</span>
                  <input type="date" value={fecha} min={getTodayStr()}
                    onChange={e => setFecha(e.target.value)}
                    disabled={readOnly}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 10,
                      border: `2px solid ${fechaFocus ? ci.cyan : c.border}`,
                      background: c.bgInput, color: c.textPrimary,
                      fontSize: 13, fontWeight: 600, fontFamily: 'inherit', outline: 'none',
                      transition: 'border-color 0.2s',
                      opacity: readOnly ? 0.6 : 1, cursor: readOnly ? 'not-allowed' : 'text',
                    }}
                    onFocus={() => setFechaFocus(true)}
                    onBlur={() => setFechaFocus(false)} />
                </div>
                <div style={{ flex: '0 0 108px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={fieldSublabel}>Hora inicio</span>
                  <CitaSelectorHora
                    value={horaInicio}
                    onChange={aplicarHoraInicio}
                    c={c}
                    isDark={isDark}
                    accentColor={ci.cyan}
                    accentBg={ci.cyanBg}
                    chipText={ci.chipText}
                    borderColor={horaFocus ? ci.cyan : c.border}
                    onFocus={() => setHoraFocus(true)}
                    onBlur={() => setHoraFocus(false)}
                    align="left"
                    disabled={readOnly}
                  />
                </div>
                <div style={{ flex: '0 0 108px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={fieldSublabel}>Hora fin</span>
                  <CitaSelectorHora
                    value={horaFin}
                    onChange={(v) => {
                      setHoraFin(v);
                      setHoraFinManual(true);
                    }}
                    c={c}
                    isDark={isDark}
                    accentColor={ci.cyan}
                    accentBg={ci.cyanBg}
                    chipText={ci.chipText}
                    borderColor={horaFinFocus ? ci.cyan : c.border}
                    onFocus={() => setHoraFinFocus(true)}
                    onBlur={() => setHoraFinFocus(false)}
                    align="right"
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>

            {/* Duración por visita */}
            <div style={sectionCard}>
              <div style={sectionLabel}>Duración por visita</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {DURACIONES.map(d => (
                  <button key={d.min} onClick={() => !readOnly && aplicarDuracion(d.min)} disabled={readOnly} style={{
                    padding: '6px 12px', borderRadius: 10,
                    border: `2px solid ${duracion === d.min ? ci.cyan : c.border}`,
                    background: duracion === d.min ? ci.cyanBg : c.bgInput,
                    color: duracion === d.min ? ci.chipText : c.textMuted,
                    fontSize: 12, fontWeight: 600, cursor: readOnly ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', transition: 'all 0.15s', opacity: readOnly ? 0.6 : 1,
                  }}>{d.label}</button>
                ))}
              </div>
            </div>

            {/* Propiedades reordenables (opcionales) */}
            <div style={sectionCard}>
              <div style={sectionLabel}>Orden de visitas <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, opacity: 0.75 }}>(opcional)</span></div>
              <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 6, fontWeight: 500, opacity: 0.6 }}>
                Arrastra para reordenar · La hora se calcula automáticamente
              </div>

              {/* Añadir propiedad por referencia o texto libre */}
              {!readOnly && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      value={addRef}
                      onChange={(e) => { setAddRef(e.target.value); if (addErr) setAddErr(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarPorRef(); } }}
                      placeholder="Ref. CRM (3461-C) o texto libre (calle, zona…)"
                      disabled={addBusy}
                      style={{
                        flex: 1, padding: '7px 10px', borderRadius: 10,
                        border: `2px solid ${c.border}`, background: c.bgInput,
                        color: c.textPrimary, fontSize: 13, fontWeight: 600,
                        fontFamily: 'inherit', outline: 'none',
                      }}
                    />
                    <button
                      type="button"
                      onClick={agregarPorRef}
                      disabled={addBusy || !addRef.trim()}
                      style={{
                        padding: '7px 14px', borderRadius: 10, border: 'none',
                        background: (addBusy || !addRef.trim()) ? c.border : `linear-gradient(135deg, ${ci.cyanFrom}, ${ci.cyanTo})`,
                        color: (addBusy || !addRef.trim()) ? c.textMuted : ci.cyanText,
                        fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                        cursor: (addBusy || !addRef.trim()) ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >
                      {addBusy ? 'Buscando…' : '+ Añadir'}
                    </button>
                    <button
                      type="button"
                      onClick={agregarComoTextoLibre}
                      disabled={addBusy || !addRef.trim()}
                      title="Guardar sin buscar en el CRM"
                      style={{
                        padding: '7px 10px', borderRadius: 10,
                        border: `1.5px solid ${c.border}`,
                        background: c.bgInput,
                        color: c.textMuted,
                        fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                        cursor: (addBusy || !addRef.trim()) ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0,
                        opacity: (addBusy || !addRef.trim()) ? 0.5 : 1,
                      }}
                    >
                      Como texto
                    </button>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 10, color: c.textMuted, fontWeight: 500, opacity: 0.75 }}>
                    Primero busca la ref en el CRM. Si no existe, usa «Como texto».
                  </div>
                  {addErr && (
                    <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: '#b91c1c' }}>{addErr}</div>
                  )}
                </div>
              )}

              {propsOrdenadas.map((p, i) => (
                <div
                  key={p.id}
                  draggable={!readOnly}
                  onDragStart={() => !readOnly && handleDragStart(i)}
                  onDragOver={e => !readOnly && handleDragOver(e, i)}
                  onDrop={() => !readOnly && handleDrop(i)}
                  onDragEnd={handleDragEnd}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '28px 52px 42px 1fr 22px',
                    alignItems: 'center',
                    columnGap: 8,
                    padding: '7px 10px', borderRadius: 10,
                    border: `1px solid ${dragOverIdx === i ? ci.cyan : c.border}`,
                    background: dragIdx === i ? ci.cyanBg : c.bgInput,
                    opacity: dragIdx === i ? 0.6 : 1,
                    cursor: readOnly ? 'default' : 'grab', transition: 'all 0.15s', marginBottom: 4,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.4, cursor: readOnly ? 'default' : 'grab' }}>
                    {[0, 1, 2].map(r => (
                      <div key={r} style={{ display: 'flex', gap: 2 }}>
                        <div style={{ width: 4, height: 4, borderRadius: 2, background: c.textMuted }} />
                        <div style={{ width: 4, height: 4, borderRadius: 2, background: c.textMuted }} />
                      </div>
                    ))}
                  </div>
                  <div style={{
                    textAlign: 'center', fontSize: 13, fontWeight: 700,
                    color: ci.cyanLight, flexShrink: 0,
                    padding: '4px 0', borderRadius: 6, background: ci.cyanBg,
                  }}>{addMinutes(horaInicio, i * duracion)}</div>
                  <div style={{
                    width: 42, height: 32, borderRadius: 6,
                    background: c.bgCard, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, color: c.textMuted, overflow: 'hidden',
                    border: `1px solid ${c.borderLight}`,
                  }}>{p.icono || '🏠'}</div>
                    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, minWidth: 0, lineHeight: 1.3 }}>
                      {isPropiedadTextoLibre(p) ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: ci.chipText,
                          background: ci.cyanBg, borderRadius: 4, padding: '1px 6px', flexShrink: 0,
                        }}>Texto</span>
                      ) : (p.referencia || p.id) ? (
                        <span style={{ flexShrink: 0 }}>
                          <CitaLinkReferencia
                            referencia={p.referencia || p.id}
                            onAbrir={onAbrirVistaPrevia ? () => onAbrirVistaPrevia(p.id) : null}
                            color={CITA_REF}
                            style={{ fontSize: 12 }}
                          />
                        </span>
                      ) : null}
                      {!isPropiedadTextoLibre(p) && (p.referencia || p.id) && (p.titulo && String(p.titulo).trim()) ? (
                        <span style={{ flexShrink: 0, color: c.textMuted, fontSize: 12 }}>·</span>
                      ) : null}
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: c.textPrimary,
                        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {(p.titulo && String(p.titulo).trim()) || 'Sin título'}
                      </span>
                    </div>
                    <div style={{ fontSize: 10.5, color: c.textMuted, fontWeight: 500, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.detalle}</div>
                    {(propTieneLlaves(p) || p.recoger_llaves_en) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                        <BadgeLlavesPropiedad p={p} />
                        {p.recoger_llaves_en && (
                          <span style={{ fontSize: 10, color: '#d97706', fontWeight: 500 }}>
                            📍 Recoger en: {p.recoger_llaves_en}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <div onClick={() => removeProp(p.id)} style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: `1px solid ${c.border}`, background: c.bgInput,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, color: c.textMuted, cursor: 'pointer', flexShrink: 0,
                    }}>✕</div>
                  )}
                </div>
              ))}

              {propsOrdenadas.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0', color: c.textMuted, fontSize: 12, fontWeight: 500 }}>
                  Sin propiedades — puedes confirmar la cita y añadirlas después
                </div>
              )}
            </div>

            {!modoEdicion && (
              <div style={sectionCard}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 10,
                  background: ci.cyanBg, border: `1px solid ${ci.cyanBorder}`,
                }}>
                  <div style={{ fontSize: 20, flexShrink: 0 }}>🗓️</div>
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: ci.chipText, lineHeight: 1.4 }}>
                      {formatFecha(fecha)} · {horaInicio} — {horaFin}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: c.textMuted, marginTop: 2 }}>
                      {propsOrdenadas.length
                        ? `${propsOrdenadas.length} visita${propsOrdenadas.length !== 1 ? 's' : ''} · ${duracion} min cada una · Total: ${propsOrdenadas.length * duracion} min`
                        : `Hueco de ${duracion} min · sin propiedades aún`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ height: 4 }} />
          </div>

          {/* Columna derecha: contactos (imagen 2) */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', background: isDark ? 'transparent' : '#d8d8d0' }}>
            <BloqueContactosVisita
              propsOrdenadas={propsOrdenadas}
              contact={contact}
              horaInicio={horaInicio}
              duracion={duracion}
              c={c}
              onLlamarTelefono={onLlamarTelefono}
              onAbrirFichaCliente={onAbrirFichaCliente}
              onAbrirFichaContacto={onAbrirFichaContacto}
              onActivarFichaDetras={activarFichaDetras}
              onAbrirVistaPrevia={onAbrirVistaPrevia}
              onPropRowRef={handlePropRowRef}
            />
          </div>

        </div>

        {errGuardar && (
          <div style={{
            margin: '0 16px 8px', padding: '10px 12px', borderRadius: 10,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
            color: '#b91c1c', fontSize: 12, fontWeight: 600, lineHeight: 1.4,
          }}>
            {errGuardar}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px 14px',
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {footerExtra}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          <button onClick={onClose} style={{
            padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
            background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>Cancelar</button>
          <button disabled={!puedeConfirmar || guardando}
            title={readOnly ? 'No tienes permiso para modificar esta cita' : undefined}
            onClick={() => { if (puedeConfirmar && !guardando) handleConfirm(); }}
            style={{
              padding: '11px 28px', borderRadius: 10, border: 'none',
              background: puedeConfirmar && !guardando
                ? `linear-gradient(135deg, ${ci.cyanFrom}, ${ci.cyanTo})`
                : c.border,
              color: puedeConfirmar && !guardando ? ci.cyanText : c.textMuted,
              cursor: puedeConfirmar && !guardando ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              boxShadow: puedeConfirmar && !guardando ? `0 4px 14px ${ci.cyanShadow}` : 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              opacity: puedeConfirmar && !guardando ? 1 : 0.5,
            }}>
            <span style={{ fontSize: 15 }}>📅</span>
            {guardando ? 'Guardando…' : (modoEdicion ? 'Guardar cambios' : 'Confirmar cita')}
          </button>
          </div>
        </div>

        </div>

        {propsOrdenadas
          .filter((p) => String(p.info_visita || '').trim())
          .map((p) => (
            <div
              key={`info-${p.id}`}
              style={{
                position: 'absolute',
                left: 'calc(100% + 16px)',
                top: infoCardTops[p.id] ?? 0,
                zIndex: zIndex + 1,
                pointerEvents: 'auto',
              }}
            >
              <InfoVisitaCard p={p} onAbrirVistaPrevia={onAbrirVistaPrevia} />
            </div>
          ))}

        </div>

      </div>
    </div>

    <ModalAvisoLlavesCitas
      isOpen={Boolean(avisoLlaves?.contexto)}
      contexto={avisoLlaves?.contexto || null}
      onClose={cerrarAvisoLlaves}
      theme={theme}
      zIndex={zIndex + 50}
    />
    </>
  );
}
